/**
 * Plan-restricted checkout coupons.
 *
 * Chargebee holds the real discount. This file mirrors the coupons whose Chargebee
 * `item_constraints` limit them to specific item prices, so that:
 *   1. the payment routes never send such a coupon with any other plan (Chargebee
 *      rejects the whole subscription create with "not applicable to any of the
 *      items", which would fail the checkout), and
 *   2. the setup page shows the exact first-cycle total Chargebee will invoice
 *      (display <-> charge parity; process_nmi_renewal charges the invoice amount_due).
 *
 * Coupons not listed here (COMEBACK20 / COMEBACK30) apply to any plan and are unaffected.
 */

export interface PlanRestrictedCoupon {
    /** Chargebee item price ids the coupon applies to (Chargebee item_constraints, plan = specific). */
    planIds: ReadonlySet<string>
    /** First-cycle invoice total in cents after the discount (list price minus the Chargebee fixed amount). */
    firstCycleTotalCents: number
}

/**
 * psexpert: Chargebee coupon `psexpert` — fixed $470.00 off `plan-glory-1188` ($1,188 -> $718),
 * duration_type one_time (first invoice only; renewals at list price), no expiry, unlimited redemptions.
 * Not advertised anywhere; only reaches /setup through the partner-migration redirect links.
 * Since the 2026-10-24 parity pricing, Glory yearly is plan-glory-720 ($720), which this coupon does
 * NOT apply to: migration links carrying coupon=psexpert are stripped at checkout and buy Glory
 * yearly at list $720 (within $2 of the old $718 promise). Kept so stale links never fail.
 */
export const PLAN_RESTRICTED_COUPONS: Readonly<Record<string, PlanRestrictedCoupon>> = {
    psexpert: { planIds: new Set(["plan-glory-1188"]), firstCycleTotalCents: 71800 },
}

/** True when the coupon may be sent to Chargebee with this plan (unrestricted coupons always pass). */
export function isCouponApplicableToPlan(coupon: string | null | undefined, planId: string | null | undefined): boolean {
    if (!coupon) return false
    const restricted = PLAN_RESTRICTED_COUPONS[coupon]
    if (!restricted) return true
    return !!planId && restricted.planIds.has(planId)
}

/** First-cycle total (cents) the coupon produces on this plan, or null when it does not change the price. */
export function getCouponFirstCycleTotalCents(coupon: string | null | undefined, planId: string | null | undefined): number | null {
    if (!coupon || !planId) return null
    const restricted = PLAN_RESTRICTED_COUPONS[coupon]
    return restricted && restricted.planIds.has(planId) ? restricted.firstCycleTotalCents : null
}
